/* ===========================================================
   KEKE — Shopify theme behaviour
   Cart drawer (Shopify Cart AJAX API) + variant picker +
   quick-add + product recommendations.
   =========================================================== */
(function () {
  'use strict';

  /* ---- money formatting ----------------------------------
     Reads the shop's money format from window.Keke.moneyFormat
     (injected in theme.liquid). Falls back to "${{amount}}". */
  function formatMoney(cents) {
    var fmt = (window.Keke && window.Keke.moneyFormat) || '${{amount}}';
    var value = '';
    function num(n, decimals) {
      n = (cents / 100).toFixed(decimals);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return decimals ? parts.join('.') : parts[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
      switch (key) {
        case 'amount': value = num(cents, 2); break;
        case 'amount_no_decimals': value = num(cents, 0); break;
        case 'amount_with_comma_separator': value = num(cents, 2).replace(/\./g, ','); break;
        case 'amount_no_decimals_with_comma_separator': value = num(cents, 0); break;
        default: value = num(cents, 2);
      }
      return value;
    });
  }

  function formatShippingMoney(cents) {
    return window.Keke.freeShippingPrefix + (cents / 100).toFixed(2);
  }

  function shippingCurrencyMatches(cart) {
    return cart.currency === (window.Keke && window.Keke.freeShippingCurrency);
  }

  /* ---- HTML escape helper --------------------------------- */
  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  /* ---- tiny fetch helpers --------------------------------- */
  function route(name, fallback) {
    return (window.Keke && window.Keke.routes && window.Keke.routes[name]) || fallback;
  }
  function jsonRoute(name, fallback) {
    var url = route(name, fallback);
    return url.replace(/\?.*$/, '').replace(/\/$/, '') + '.js';
  }
  function fetchJson(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (payload) {
        if (!response.ok) {
          var error = new Error(payload.description || payload.message || 'Request failed.');
          error.payload = payload;
          error.response = response;
          throw error;
        }
        return payload;
      });
    });
  }
  function getCart() {
    return fetchJson(jsonRoute('cart', '/cart'), { headers: { 'Accept': 'application/json' } });
  }
  function addItem(id, qty) {
    return fetchJson(jsonRoute('cart_add', '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty || 1 }] })
    });
  }
  function changeItem(key, qty) {
    return fetchJson(jsonRoute('cart_change', '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }
  function text(key, fallback) {
    return (window.Keke && window.Keke.strings && window.Keke.strings[key]) || fallback;
  }
  function fill(template, values) {
    return String(template).replace(/%([A-Z]+)%/g, function (m, k) {
      return values && values[k] !== undefined ? values[k] : m;
    });
  }
  function itemsLabel(count) {
    return count + ' ' + (count === 1 ? text('cart_item_singular', 'item') : text('cart_item_plural', 'items'));
  }

  /* ---- scroll-lock counter (shared by cart + mobile nav) -- */
  var _lockCount = 0;
  function lockScroll() { if (++_lockCount === 1) document.body.style.overflow = 'hidden'; }
  function unlockScroll() { if (--_lockCount <= 0) { _lockCount = 0; document.body.style.overflow = ''; } }

  /* ---- cart drawer ---------------------------------------- */
  var _cartOpener = null;
  var _cartInertSiblings = [];
  function cartFocusables(drawer) {
    return Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (el) {
      return el.getClientRects().length > 0;
    });
  }
  function setCartSiblingsInert(drawer, shouldInert) {
    if (shouldInert) {
      var drawerRoot = drawer;
      while (drawerRoot.parentElement && drawerRoot.parentElement !== document.body) {
        drawerRoot = drawerRoot.parentElement;
      }
      _cartInertSiblings = Array.from(document.body.children).filter(function (el) {
        return el !== drawerRoot && !el.classList.contains('cart-drawer-scrim');
      }).map(function (el) {
        var state = {
          element: el,
          hadInert: el.hasAttribute('inert'),
          ariaHidden: el.getAttribute('aria-hidden')
        };
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
        return state;
      });
      return;
    }
    _cartInertSiblings.forEach(function (state) {
      if (!state.hadInert) state.element.removeAttribute('inert');
      if (state.ariaHidden === null) state.element.removeAttribute('aria-hidden');
      else state.element.setAttribute('aria-hidden', state.ariaHidden);
    });
    _cartInertSiblings = [];
  }
  function openCart() {
    _cartOpener = document.activeElement;
    document.querySelector('.cart-drawer-scrim')?.classList.add('is-open');
    var drawer = document.querySelector('.cart-drawer');
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.removeAttribute('aria-hidden');
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-modal', 'true');
    }
    lockScroll();
    if (drawer) setCartSiblingsInert(drawer, true);
    setTimeout(function () { document.querySelector('.cart-close')?.focus(); }, 50);
  }
  function closeCart() {
    document.querySelector('.cart-drawer-scrim')?.classList.remove('is-open');
    var drawer = document.querySelector('.cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      drawer.removeAttribute('aria-modal');
    }
    if (drawer) setCartSiblingsInert(drawer, false);
    unlockScroll();
    if (_cartOpener && _cartOpener.focus) _cartOpener.focus();
    _cartOpener = null;
  }

  function cartLineImage(it) {
    return it.image ? it.image.replace(/(\.[^.\/]+)(\?|$)/, '_120x120$1$2') : '';
  }

  function buildCartLine(it) {
    var el = document.createElement('div');
    el.className = 'cart-drawer-item';
    el.dataset.key = it.key;
    el._kkItem = it;
    var url = esc(it.url || '#');
    el.innerHTML =
      '<div class="thumb"><a class="cart-item-link" href="' + url + '" tabindex="-1" aria-hidden="true"><img src="' + esc(cartLineImage(it)) + '" alt="' + esc(it.product_title) + '"></a></div>' +
      '<div>' +
        '<div class="nm"><a class="cart-item-link" href="' + url + '">' + esc(it.product_title) + '</a></div>' +
        '<div class="cart-item-option"></div>' +
        '<div class="qty cart-item-controls" aria-label="' + esc(text('cart_quantity_controls', 'Quantity controls')) + '">' +
          '<button data-act="dec" aria-label="' + esc(fill(text('cart_decrease_for', 'Decrease quantity for %TITLE%'), { TITLE: it.product_title })) + '">−</button>' +
          '<span></span>' +
          '<button data-act="inc" aria-label="' + esc(fill(text('cart_increase_for', 'Increase quantity for %TITLE%'), { TITLE: it.product_title })) + '">+</button>' +
          '<button class="cart-item-remove" data-act="rm" aria-label="' + esc(fill(text('cart_remove_for', 'Remove %TITLE%'), { TITLE: it.product_title })) + '">' + esc(text('cart_remove', 'Remove')) + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="pr"><span></span><small></small></div>';
    el.querySelector('[data-act="inc"]').onclick = function () { mutate(el._kkItem.key, el._kkItem.quantity + 1, 'inc', this); };
    el.querySelector('[data-act="dec"]').onclick = function () { mutate(el._kkItem.key, el._kkItem.quantity - 1, 'dec', this); };
    el.querySelector('[data-act="rm"]').onclick = function () { mutate(el._kkItem.key, 0, 'rm', this); };
    updateCartLine(el, it);
    return el;
  }

  function updateCartLine(el, it) {
    var img = el.querySelector('.thumb img');
    var src = cartLineImage(it);
    if (img && img.getAttribute('src') !== src) img.setAttribute('src', src);
    var option = el.querySelector('.cart-item-option');
    if (option) {
      var hasOption = it.variant_title && it.variant_title !== 'Default Title';
      option.hidden = !hasOption;
      option.textContent = hasOption ? it.variant_title : '';
    }
    var qty = el.querySelector('.qty span');
    if (qty) qty.textContent = it.quantity;
    var price = el.querySelector('.pr > span');
    if (price) price.textContent = formatMoney(it.final_line_price);
    var each = el.querySelector('.pr > small');
    if (each) {
      each.hidden = !(it.quantity > 1);
      each.textContent = it.quantity > 1 ? formatMoney(it.final_price) + ' ' + text('cart_each', 'each') : '';
    }
  }

  function renderCart(cart, shouldAnnounce) {
    updateBadge(cart.item_count);
    updateFreeShipping(cart);

    var shopUrl = (window.Keke && window.Keke.routes && window.Keke.routes.all_products) || '/collections/all';
    var drawer = document.querySelector('.cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);

    var itemsEl = document.querySelector('.cart-drawer .items');
    if (itemsEl) {
      // Reuse the line elements built by an earlier render so a quantity change
      // updates numbers in place instead of recreating every card and image.
      var built = {};
      Array.prototype.forEach.call(itemsEl.children, function (child) {
        if (child._kkItem && child.dataset.key) built[child.dataset.key] = child;
      });
      var ordered = cart.items.map(function (it) {
        var el = built[it.key];
        if (el) {
          el._kkItem = it;
          updateCartLine(el, it);
          return el;
        }
        return buildCartLine(it);
      });
      Array.prototype.slice.call(itemsEl.children).forEach(function (child) {
        if (ordered.indexOf(child) === -1) itemsEl.removeChild(child);
      });
      ordered.forEach(function (el, index) {
        if (itemsEl.children[index] !== el) itemsEl.insertBefore(el, itemsEl.children[index] || null);
      });
      if (!cart.items.length) {
        itemsEl.innerHTML =
          '<div class="cart-empty-state">' +
            '<div class="cart-empty-title">' + esc(text('cart_empty_title', 'Your nest is empty.')) + '</div>' +
            '<p>' + esc(text('cart_empty_text', 'Find something playful, quiet, or cozy to bring home.')) + '</p>' +
            '<a class="btn full" href="' + esc(shopUrl) + '">' + esc(text('cart_shop_button', 'Shop the Collection')) + '</a>' +
          '</div>';
      }
    }

    var sub = document.querySelector('.cart-drawer .subtotal .amt');
    if (sub) sub.textContent = formatMoney(cart.items_subtotal_price);
    var cnt = document.querySelector('.cart-drawer .subtotal .count');
    if (cnt) cnt.textContent = itemsLabel(cart.item_count);
    var hdrCnt = document.querySelector('.cart-drawer header .count');
    if (hdrCnt) hdrCnt.textContent = itemsLabel(cart.item_count);
    if (shouldAnnounce) announceCart(cart);
  }

  function updateFreeShipping(cart) {
    var box = document.querySelector('[data-free-shipping-progress]');
    if (!box) return;
    var configured = window.Keke && window.Keke.freeShippingThresholdCents;
    var threshold = parseInt(configured || box.getAttribute('data-threshold'), 10) || 0;
    var textEl = box.querySelector('[data-free-shipping-text]');
    var bar = box.querySelector('[data-free-shipping-bar]');
    if (!threshold || !shippingCurrencyMatches(cart)) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    var subtotal = cart.items_subtotal_price || 0;
    var remaining = Math.max(0, threshold - subtotal);
    var progress = Math.min(100, Math.max(0, Math.round((subtotal / threshold) * 100)));
    if (textEl) {
      textEl.textContent = remaining > 0
        ? fill(text('cart_free_shipping_remaining', 'You are %AMOUNT% away from free shipping.'), { AMOUNT: formatShippingMoney(remaining) })
        : text('cart_free_shipping_unlocked', 'You have unlocked free shipping.');
    }
    if (bar) bar.style.width = progress + '%';
  }

  function announceCart(cart) {
    var status = document.querySelector('[data-cart-status]');
    if (!status) return;
    var threshold = parseInt((window.Keke && window.Keke.freeShippingThresholdCents) || 0, 10) || 0;
    var subtotal = cart.items_subtotal_price || 0;
    var message = fill(text('cart_announce_updated', 'Cart updated. %ITEMS%. Subtotal %SUBTOTAL%.'), {
      ITEMS: itemsLabel(cart.item_count),
      SUBTOTAL: formatMoney(subtotal)
    });
    if (threshold && shippingCurrencyMatches(cart)) {
      var remaining = Math.max(0, threshold - subtotal);
      message += remaining > 0
        ? ' ' + fill(text('cart_free_shipping_remaining', 'You are %AMOUNT% away from free shipping.'), { AMOUNT: formatShippingMoney(remaining) })
        : ' ' + text('cart_announce_free_unlocked', 'Free shipping unlocked.');
    }
    status.textContent = message;
  }

  function cartControl(key, action) {
    var item = Array.from(document.querySelectorAll('.cart-drawer-item')).find(function (el) {
      return el.getAttribute('data-key') === key;
    });
    return item && item.querySelector('[data-act="' + action + '"]');
  }

  function announceCartError(error) {
    var status = document.querySelector('[data-cart-status]');
    if (!status) return;
    status.textContent = (error && error.payload && (error.payload.description || error.payload.message)) || text('cart_update_error', 'Unable to update the cart. Please try again.');
  }

  function mutate(key, qty, action, opener) {
    changeItem(key, qty)
      .then(function (cart) {
        renderCart(cart, true);
        var nextAction = action === 'rm' ? 'dec' : action;
        var target = cartControl(key, nextAction) || document.querySelector('.cart-close');
        if (target) target.focus();
      })
      .catch(function (error) {
        announceCartError(error);
        if (opener && opener.focus) opener.focus();
      });
  }

  /* ---- cart page: change quantities without reloading ---- */
  function wireCartPage() {
    var root = document.querySelector('[data-cart-page]');
    if (!root) return;
    var status = root.querySelector('[data-cart-page-status]');

    function say(message) { if (status) status.textContent = message; }

    function showEmpty() {
      var shopUrl = root.getAttribute('data-shop-url') || '/collections/all';
      Array.prototype.slice.call(root.children).forEach(function (child) {
        if (child.tagName !== 'H1' && !child.hasAttribute('data-cart-page-status')) root.removeChild(child);
      });
      var p = document.createElement('p');
      p.className = 't-body-lg';
      p.style.cssText = 'margin:24px 0 32px;';
      p.textContent = text('cart_page_empty', 'Your nest is empty — let’s fix that.');
      var a = document.createElement('a');
      a.className = 'btn lg';
      a.href = shopUrl;
      a.textContent = text('cart_shop_button', 'Shop the Collection');
      root.appendChild(p);
      root.appendChild(a);
    }

    function applyCart(cart) {
      updateBadge(cart.item_count);
      renderCart(cart, false);
      if (!cart.items.length) {
        showEmpty();
        say(root.getAttribute('data-status-empty') || 'Your cart is now empty.');
        return;
      }
      var lines = {};
      Array.prototype.forEach.call(root.querySelectorAll('[data-cart-line]'), function (el) { lines[el.getAttribute('data-key')] = el; });
      Object.keys(lines).forEach(function (key) {
        var kept = cart.items.some(function (it) { return it.key === key; });
        if (!kept && lines[key].parentNode) lines[key].parentNode.removeChild(lines[key]);
      });
      cart.items.forEach(function (it, index) {
        var el = lines[it.key];
        if (!el) return;
        var qty = el.querySelector('[data-cart-qty]');
        if (qty) qty.textContent = it.quantity;
        var price = el.querySelector('[data-cart-line-price]');
        if (price) price.textContent = formatMoney(it.final_line_price);
        Array.prototype.forEach.call(el.querySelectorAll('button[form^="cart-change-"]'), function (btn) {
          var form = document.getElementById(btn.getAttribute('form'));
          if (!form) return;
          var line = form.querySelector('[name="line"]');
          var quantity = form.querySelector('[name="quantity"]');
          if (line) line.value = index + 1;
          if (quantity) {
            var id = form.id;
            quantity.value = /-inc$/.test(id) ? it.quantity + 1 : /-dec$/.test(id) ? Math.max(0, it.quantity - 1) : 0;
          }
        });
      });
      var subtotal = root.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = fill(text('cart_subtotal_with', 'Subtotal · %AMOUNT%'), { AMOUNT: formatMoney(cart.total_price) });
      say(fill(text('cart_announce_updated', 'Cart updated. %ITEMS%. Subtotal %SUBTOTAL%.'), {
        ITEMS: itemsLabel(cart.item_count),
        SUBTOTAL: formatMoney(cart.total_price)
      }));
    }

    root.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('button[form^="cart-change-"]') : null;
      if (!btn || !root.contains(btn)) return;
      var form = document.getElementById(btn.getAttribute('form'));
      var lineEl = btn.closest('[data-cart-line]');
      var quantityInput = form && form.querySelector('[name="quantity"]');
      if (!form || !lineEl || !quantityInput) return;
      e.preventDefault();
      if (lineEl.getAttribute('aria-busy') === 'true') return;
      var key = lineEl.getAttribute('data-key');
      var target = parseInt(quantityInput.value, 10) || 0;
      var action = /-inc$/.test(form.id) ? 'inc' : /-dec$/.test(form.id) ? 'dec' : 'rm';
      var buttons = Array.prototype.slice.call(lineEl.querySelectorAll('button[form^="cart-change-"]'));
      lineEl.setAttribute('aria-busy', 'true');
      buttons.forEach(function (b) { b.disabled = true; });
      function done() {
        lineEl.removeAttribute('aria-busy');
        buttons.forEach(function (b) { b.disabled = false; });
      }
      changeItem(key, target)
        .then(function (cart) {
          done();
          applyCart(cart);
          var again = lineEl.parentNode && lineEl.querySelector('button[form$="-' + (action === 'rm' ? 'dec' : action) + '"]');
          var fallback = root.querySelector('button[form^="cart-change-"]') || root.querySelector('a.btn');
          var next = again || fallback;
          if (next && next.focus) next.focus();
        })
        .catch(function (error) {
          done();
          if (error && error.response) {
            say(error.message || text('cart_update_error', 'Unable to update the cart. Please try again.'));
          } else {
            form.submit(); // Network failure: fall back to the regular POST.
          }
        });
    });
  }

  function updateBadge(count) {
    document.querySelectorAll('.kk-cart-count').forEach(function (el) {
      el.textContent = count;
      el.style.display = count ? 'inline-flex' : 'none';
    });
  }

  /* ---- fly to cart ---------------------------------------- */
  function flyToCart(fromEl, imgSrc) {
    var cartIcon = document.querySelector('.kk-cart-btn');
    if (!cartIcon || !fromEl) return;
    var a = fromEl.getBoundingClientRect();
    var b = cartIcon.getBoundingClientRect();
    var dot = document.createElement('div');
    dot.className = 'fly-cart';
    if (imgSrc) {
      var img = document.createElement('img');
      img.src = imgSrc;
      img.alt = '';
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:999px;';
      dot.appendChild(img);
    } else {
      dot.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 5h14l-1.5 9H8L6 3H3"/><circle cx="9" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/></svg>';
    }
    dot.style.left = a.left + a.width / 2 - 18 + 'px';
    dot.style.top = a.top + a.height / 2 - 18 + 'px';
    document.body.appendChild(dot);
    requestAnimationFrame(function () {
      dot.style.left = b.left + b.width / 2 - 18 + 'px';
      dot.style.top = b.top + b.height / 2 - 18 + 'px';
      dot.style.transform = 'scale(.3)';
      dot.style.opacity = '0';
    });
    setTimeout(function () { dot.remove(); }, 850);
    cartIcon.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.25) rotate(-8deg)' }, { transform: 'scale(1)' }],
      { duration: 450, easing: 'cubic-bezier(.3,1.6,.4,1)' }
    );
  }

  /* ---- add to cart (quick-add + PDP) ----------------------
     Delegated so forms injected later (e.g. recommendations)
     work without re-wiring. */
  function wireAddForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target && e.target.closest ? e.target.closest('form[data-add-form]') : null;
      if (!form) return;
      e.preventDefault();
      var idInput = form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var btn = form.querySelector('[type="submit"]');
      var imgSrc = form.getAttribute('data-image') || '';
      var itemAdded = false;
      if (btn) btn.classList.add('is-loading');
      addItem(idInput.value, qty)
        .then(function () {
          itemAdded = true;
          if (btn) btn.classList.remove('is-loading');
          flyToCart(btn || form, imgSrc);
          return getCart().then(function (cart) { renderCart(cart, true); });
        })
        .catch(function (error) {
          if (btn) btn.classList.remove('is-loading');
          if ((error && error.response) || itemAdded) {
            var msg = itemAdded
              ? text('cart_refresh_error', 'Item added, but the cart could not be refreshed. Please open your cart.')
              : (error.payload && (error.payload.description || error.payload.message)) || text('cart_add_error', 'Could not add to cart. Please try again.');
            var oldErr = form.querySelector('.add-error');
            if (form._kkErrTimer) {
              clearTimeout(form._kkErrTimer);
              form._kkErrTimer = null;
            }
            if (oldErr && oldErr.parentNode) {
              oldErr.parentNode.removeChild(oldErr);
            }
            var err = document.createElement('p');
            err.className = 'add-error';
            err.setAttribute('role', 'alert');
            err.style.cssText = 'color:#b91c1c;font-size:14px;margin:6px 0 0;font-family:var(--font-body);';
            form.appendChild(err);
            err.textContent = msg;
            form._kkErrTimer = setTimeout(function () {
              if (err.parentNode) err.parentNode.removeChild(err);
              form._kkErrTimer = null;
            }, 4000);
            return;
          }
          form.submit(); // Only an add-request transport failure falls back to Shopify's native cart page.
        });
    });
  }

  /* ---- wishlist (device-local persistence) ----------------
     Account sync requires an authenticated app/backend; see the committed
     implementation prerequisites before replacing this client-only store. */
  function wishList() {
    try {
      var saved = JSON.parse(localStorage.getItem('kk-wish') || '[]');
      if (!Array.isArray(saved)) return [];
      return saved.filter(function (item) {
        return typeof item === 'string' || typeof item === 'number';
      });
    } catch (e) { return []; }
  }
  function wishSave(list) {
    try { localStorage.setItem('kk-wish', JSON.stringify(list)); } catch (e) {}
  }
  function wireWish() {
    function setWishState(el, isSaved) {
      el.classList.toggle('is-saved', isSaved);
      el.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      el.setAttribute('aria-label', isSaved
        ? el.getAttribute('data-wishlist-remove-label')
        : el.getAttribute('data-wishlist-add-label'));
    }

    // Restore saved state on page load. The same product can appear more than
    // once on a page, so update every matching control together.
    var saved = wishList();
    document.querySelectorAll('.wish[data-pid], .pdp-wish[data-pid]').forEach(function (el) {
      setWishState(el, saved.indexOf(el.getAttribute('data-pid')) !== -1);
    });

    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('.wish[data-pid], .pdp-wish[data-pid]') : null;
      if (!el) return;
      e.preventDefault();
      var pid = el.getAttribute('data-pid');
      var list = wishList();
      var idx = list.indexOf(pid);
      if (idx === -1) list.push(pid);
      else list.splice(idx, 1);
      wishSave(list);
      document.querySelectorAll('.wish[data-pid], .pdp-wish[data-pid]').forEach(function (control) {
        if (control.getAttribute('data-pid') === pid) setWishState(control, idx === -1);
      });
    });
  }

  /* ---- PDP variant picker --------------------------------- */
  function wireVariantPicker() {
    var root = document.querySelector('[data-product-json]');
    if (!root) return;
    var product;
    try { product = JSON.parse(root.textContent); } catch (e) { return; }

    var form = document.querySelector('form[data-add-form][data-pdp]');
    if (!form) return;
    var idInput = form.querySelector('[name="id"]');
    var priceEl = document.querySelector('.pdp-price');
    var compareEl = document.querySelector('.pdp-price-old');
    var addBtn = form.querySelector('[type="submit"]');

    var selected = {};
    // Shopify resolves ?variant= before rendering this form. Respect that
    // server-selected variant instead of replacing a shared/deep link with
    // the first available option in JavaScript.
    var selectedVariant = product.variants.find(function (v) {
      return String(v.id) === String(idInput.value);
    }) || product.variants.find(function (v) { return v.available; }) || product.variants[0];
    if (selectedVariant) { selectedVariant.options.forEach(function (o, i) { selected[i] = o; }); }

    function setGalleryCurrent(mediaId) {
      if (!mediaId) return;
      var thumb = Array.from(document.querySelectorAll('.gallery-thumbs button')).find(function (button) {
        return String(button.getAttribute('data-media-id')) === String(mediaId);
      });
      if (!thumb) return;
      document.querySelectorAll('.gallery-thumbs button').forEach(function (button) {
        button.classList.toggle('on', button === thumb);
        if (button === thumb) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    function resolve(updateUrl) {
      var match = product.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === selected[i]; });
      });
      if (!match) return;
      idInput.value = match.id;
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      if (compareEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compareEl.textContent = formatMoney(match.compare_at_price);
          compareEl.style.display = '';
        } else { compareEl.style.display = 'none'; }
      }
      if (addBtn) {
        addBtn.disabled = !match.available;
        var textNode = Array.from(addBtn.childNodes).find(function (n) { return n.nodeType === 3; });
        if (textNode) {
          textNode.nodeValue = match.available ? 'Add to Cart ' : 'Sold out ';
        }
      }
      // Disable values that cannot make an available variant with the other
      // current selections. Keep an unavailable selected value focusable so
      // its sold-out state remains legible and reversible.
      document.querySelectorAll('[data-option-index]').forEach(function (optBtn) {
        var oIdx = parseInt(optBtn.getAttribute('data-option-index'), 10);
        var oVal = optBtn.getAttribute('data-option-value');
        var avail = product.variants.some(function (v) {
          return v.available && v.options.every(function (optionValue, index) {
            return index === oIdx ? optionValue === oVal : optionValue === selected[index];
          });
        });
        var isSelected = selected[oIdx] === oVal;
        optBtn.classList.toggle('unavailable', !avail);
        optBtn.disabled = !avail && !isSelected;
      });
      // update gallery to variant image
      if (match.featured_image && match.featured_image.src) {
        var g = document.getElementById('gallery-img');
        if (g) {
          g.removeAttribute('srcset');
          g.src = match.featured_image.src.replace(/(\.[^.\/]+)(\?|$)/, '_1200x1500$1$2');
          g.style.display = '';
          document.querySelectorAll('.gallery-video').forEach(function (v) {
            v.style.display = 'none';
            var ve = v.querySelector('video'); if (ve) ve.pause();
          });
        }
      }
      if (match.featured_media && match.featured_media.id) setGalleryCurrent(match.featured_media.id);
      if (updateUrl && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    document.querySelectorAll('[data-option-index]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-option-value');
        selected[idx] = val;
        // toggle active state within this option group
        btn.parentElement.querySelectorAll('[data-option-index="' + idx + '"]').forEach(function (s) { s.classList.remove('on'); });
        btn.classList.add('on');
        resolve(true);
      });
    });
    resolve();
  }

  /* ---- PDP quantity stepper -------------------------------
     Keeps the visible counter and the hidden form input in sync. */
  function wireQtyStepper() {
    document.querySelectorAll('.qty-picker').forEach(function (picker) {
      var hidden = picker.querySelector('input[name="quantity"]') || picker.parentElement.querySelector('input[name="quantity"]');
      var display = picker.querySelector('.v');
      picker.querySelectorAll('button').forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          var dec = b.getAttribute('data-step') === 'down';
          var cur = parseInt((hidden && hidden.value) || (display && display.textContent), 10) || 1;
          var next = Math.max(1, cur + (dec ? -1 : 1));
          if (hidden) hidden.value = next;
          if (display) display.textContent = next;
        });
      });
    });
  }

  /* ---- gallery thumbs ------------------------------------- */
  function wireGallery() {
    function hideGalleryVideos(resetTime) {
      document.querySelectorAll('.gallery-video').forEach(function (v) {
        v.style.display = 'none';
        var ve = v.querySelector('video');
        if (ve) {
          ve.pause();
          if (resetTime) ve.currentTime = 0;
        }
      });
    }

    function showGalleryImage(img, src, srcset, alt) {
      if (!img) return;
      img.style.display = '';
      if (srcset) img.srcset = srcset;
      else img.removeAttribute('srcset');
      if (src) img.src = src;
      if (alt) img.alt = alt;
    }

    document.querySelectorAll('.gallery-thumbs button').forEach(function (b) {
      b.addEventListener('click', function () {
        var type = b.getAttribute('data-media-type') || 'image';
        var img = document.getElementById('gallery-img');
        document.querySelectorAll('.gallery-thumbs button').forEach(function (x) {
          x.classList.toggle('on', x === b);
          if (x === b) x.setAttribute('aria-current', 'true');
          else x.removeAttribute('aria-current');
        });
        if (type === 'video') {
          var vid = document.getElementById('gallery-video-' + b.getAttribute('data-video-id'));
          hideGalleryVideos(false);
          if (img) img.style.display = 'none';
          if (vid && vid.style) {
            vid.style.display = '';
            var ve = vid.querySelector('video'); if (ve) ve.play().catch(function () {});
          }
        } else {
          var src = b.getAttribute('data-full') || (b.firstElementChild && b.firstElementChild.src);
          var srcset = b.getAttribute('data-srcset');
          var alt = b.getAttribute('data-alt') || (b.firstElementChild && b.firstElementChild.alt);
          hideGalleryVideos(true);
          showGalleryImage(img, src, srcset, alt);
        }
      });
    });
    // keyboard arrow navigation between thumbnails
    var allThumbs = Array.from(document.querySelectorAll('.gallery-thumbs button'));
    allThumbs.forEach(function (btn, idx) {
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' && idx < allThumbs.length - 1) { e.preventDefault(); allThumbs[idx + 1].click(); allThumbs[idx + 1].focus(); }
        if (e.key === 'ArrowLeft' && idx > 0) { e.preventDefault(); allThumbs[idx - 1].click(); allThumbs[idx - 1].focus(); }
      });
    });
  }

  /* ---- product recommendations ----------------------------
     Fetches the keke-recommendations section through the
     Section Rendering API and injects the resulting cards. */
  function loadRecommendations() {
    var el = document.querySelector('[data-keke-recommendations]');
    if (!el) return;
    var base = el.getAttribute('data-url') || '/recommendations/products';
    var url = base + '?section_id=keke-recommendations&product_id=' + el.getAttribute('data-product-id') + '&limit=' + (el.getAttribute('data-limit') || 4);
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = doc.querySelector('.related-grid');
        var target = el.querySelector('[data-grid]');
        if (grid && grid.children.length && target) {
          target.innerHTML = '';
          Array.from(grid.children).forEach(function (child) {
            target.appendChild(document.importNode(child, true));
          });
          el.hidden = false;
        }
      })
      .catch(function () { /* leave hidden */ });
  }

  /* ---- cart open triggers --------------------------------- */
  function wireCartTriggers() {
    document.querySelectorAll('.kk-cart-btn').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); openCart(); });
    });
    document.querySelector('.cart-drawer-scrim')?.addEventListener('click', closeCart);
    document.querySelector('.cart-close')?.addEventListener('click', closeCart);
    document.addEventListener('keydown', function (e) {
      var drawer = document.querySelector('.cart-drawer');
      if (!drawer || !drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeCart();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusable = cartFocusables(drawer);
      if (!focusable.length) {
        e.preventDefault();
        drawer.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---- mobile nav drawer ---------------------------------- */
  function wireMobileNav() {
    var btn = document.querySelector('.kk-menu-btn');
    var nav = document.getElementById('kk-mobile-nav');
    var scrim = document.querySelector('.kk-mobile-nav-scrim');
    var closeBtn = document.querySelector('.kk-mobile-nav-close');
    if (!btn || !nav) return;
    var opener = null;
    var inertSiblings = [];

    function setSiblingsInert(shouldInert) {
      if (shouldInert) {
        var candidates = [];
        var parent = nav.parentElement;
        while (parent) {
          Array.from(parent.children).forEach(function (el) {
            // Keep the active drawer/scrim and each ancestor that contains
            // them interactive. Shopify wraps section output in a container,
            // so making that wrapper inert would also disable the drawer.
            if (!el.contains(nav) && (!scrim || !el.contains(scrim))) candidates.push(el);
          });
          if (parent === document.body) break;
          parent = parent.parentElement;
        }
        inertSiblings = candidates.filter(function (el, index) {
          return candidates.indexOf(el) === index;
        }).map(function (el) {
          var state = { element: el, hadInert: el.hasAttribute('inert'), ariaHidden: el.getAttribute('aria-hidden') };
          el.setAttribute('inert', '');
          el.setAttribute('aria-hidden', 'true');
          return state;
        });
        return;
      }
      inertSiblings.forEach(function (state) {
        if (!state.hadInert) state.element.removeAttribute('inert');
        if (state.ariaHidden === null) state.element.removeAttribute('aria-hidden');
        else state.element.setAttribute('aria-hidden', state.ariaHidden);
      });
      inertSiblings = [];
    }

    function openNav() {
      var cart = document.querySelector('.cart-drawer');
      if (cart && cart.classList.contains('is-open')) closeCart();
      opener = document.activeElement;
      nav.classList.add('is-open');
      scrim?.classList.add('is-open');
      nav.removeAttribute('aria-hidden');
      nav.inert = false;
      nav.setAttribute('aria-modal', 'true');
      lockScroll();
      setSiblingsInert(true);
      btn.setAttribute('aria-expanded', 'true');
      if (closeBtn) closeBtn.focus();
      else nav.focus();
    }
    function closeNav(restoreFocus) {
      nav.classList.remove('is-open');
      scrim?.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      nav.inert = true;
      nav.removeAttribute('aria-modal');
      setSiblingsInert(false);
      unlockScroll();
      btn.setAttribute('aria-expanded', 'false');
      if (restoreFocus !== false && opener && opener.focus) opener.focus();
      opener = null;
    }
    closeNav(false);
    btn.addEventListener('click', openNav);
    closeBtn?.addEventListener('click', function () { closeNav(); });
    scrim?.addEventListener('click', function () { closeNav(); });
    document.addEventListener('keydown', function (e) {
      if (!nav.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeNav();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusable = cartFocusables(nav);
      if (!focusable.length) {
        e.preventDefault();
        nav.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || !nav.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !nav.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---- collapsible promo bar (mobile) --------------------
     The announcement bar lives inside the sticky header, so on
     small screens it folds away once the page scrolls and a
     chevron tab takes its place. Collapsing changes the height
     of content *above* the viewport, so the scroll position is
     nudged frame by frame to keep the page visually still. */
  /* Keeps --kk-header-h in sync with the sticky header's real height, so CSS
     scroll-padding-top can stop an in-page anchor jump (e.g. "Skip to
     content") from landing underneath the fixed header. Height changes
     whenever the mobile promo bar collapses/expands, so this has to track
     continuously rather than being measured once at load. */
  function wireHeaderOffset() {
    var header = document.getElementById('shopify-section-header');
    if (!header) return;
    var root = document.documentElement;
    function sync() {
      root.style.setProperty('--kk-header-h', Math.ceil(header.getBoundingClientRect().height) + 'px');
    }
    if (window.ResizeObserver) {
      new ResizeObserver(sync).observe(header);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }
    sync();
  }

  function wirePromoCollapse() {
    var header = document.querySelector('.kk-header');
    var promo = document.getElementById('kk-promo-bar');
    var toggle = document.querySelector('[data-kk-promo-toggle]');
    if (!header || !promo || !toggle) return;

    var mobile = window.matchMedia('(max-width: 899px)');
    var REOPEN_AT = 8;      /* back at the top -> the bar returns on its own */
    var DURATION = 280;     /* keep in sync with the CSS transition */
    var collapsed = false;
    var pinned = false;     /* visitor re-opened it: leave it open until the top */
    var collapseAt = 96;    /* scroll depth that folds the bar away */
    var frame = null;
    var settle = null;

    /* Measured on the inner element: the promo itself may be clipped to 0. */
    function naturalHeight() {
      var inner = promo.firstElementChild;
      return inner ? inner.getBoundingClientRect().height : promo.getBoundingClientRect().height;
    }

    /* Folding the bar shortens the sticky header, which drags the scroll
       position back with it. Only start deep enough that the corrected
       position stays clear of REOPEN_AT, or the two states would keep
       trading places on a tall bar. */
    function measure() {
      collapseAt = Math.max(96, Math.ceil(naturalHeight()) + 32);
    }

    function stopTracking() {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    }

    /* Keeps page content still while the header shrinks or grows. Some
       browsers already do this natively (scroll anchoring), so each frame
       we only apply what is left over — otherwise the two corrections
       stack up and the page drifts. */
    function trackHeight() {
      stopTracking();
      var last = promo.getBoundingClientRect().height;
      var expected = window.scrollY;
      (function step() {
        var height = promo.getBoundingClientRect().height;
        var want = height - last;                /* scroll delta that holds the page still */
        var applied = window.scrollY - expected; /* already applied by the browser */
        if (Math.abs(applied) > Math.abs(want) + 8) {
          frame = null;                          /* the visitor scrolled: bow out */
          return;
        }
        if (Math.abs(want - applied) > 0.5) window.scrollBy(0, want - applied);
        expected = window.scrollY;
        last = height;
        frame = requestAnimationFrame(step);
      })();
    }

    /* `skipTrack` opts out of the scroll-holding compensation below. It's
       for the two boundary cases where the header changes height but there
       is no scroll position worth preserving: arriving back at the very top
       (nothing above the header to hold still for) and a desktop/mobile
       breakpoint switch (a layout change, not a scroll event). Without this,
       trackHeight() fights the browser's own scroll anchoring right as the
       visitor reaches y=0, and the two corrections stack up into an
       overshoot that strands them a few dozen pixels below the real top. */
    function setCollapsed(next, skipTrack) {
      if (next === collapsed) return;
      collapsed = next;
      header.classList.toggle('is-promo-collapsed', next);
      toggle.setAttribute('aria-expanded', next ? 'false' : 'true');
      if (next) {
        promo.setAttribute('inert', '');
        promo.setAttribute('aria-hidden', 'true');
      } else {
        promo.removeAttribute('inert');
        promo.removeAttribute('aria-hidden');
      }
      var from = promo.getBoundingClientRect().height;
      var to = next ? 0 : naturalHeight();
      promo.style.maxHeight = from + 'px';
      void promo.offsetHeight; /* flush the start height before moving on */
      promo.style.maxHeight = to + 'px';
      clearTimeout(settle);
      settle = setTimeout(function () {
        promo.style.maxHeight = next ? '0px' : '';
        stopTracking();
      }, DURATION + 60);
      if (!skipTrack) trackHeight();
    }

    function onScroll() {
      if (frame) return; /* mid-transition: ignore the scrolling we generate */
      if (!mobile.matches) {
        pinned = false;
        if (collapsed) setCollapsed(false, true);
        return;
      }
      var y = window.scrollY || window.pageYOffset || 0;
      if (y <= REOPEN_AT) {
        pinned = false;
        if (collapsed) setCollapsed(false, true);
        return;
      }
      if (!collapsed && !pinned && y > collapseAt) setCollapsed(true);
    }

    toggle.addEventListener('click', function () {
      if (!collapsed) return;
      pinned = true;
      setCollapsed(false);
    });

    var queued = false;
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        onScroll();
      });
    }, { passive: true });
    window.addEventListener('resize', function () {
      measure();
      onScroll();
    }, { passive: true });
    if (mobile.addEventListener) mobile.addEventListener('change', function () { measure(); onScroll(); });
    else if (mobile.addListener) mobile.addListener(function () { measure(); onScroll(); });
    measure();
    onScroll();
  }

  window.Keke = window.Keke || {};
  window.Keke.openCart = openCart;
  window.Keke.refresh = function () { getCart().then(function (cart) { renderCart(cart, false); }); };

  /* ---- announcement ribbon --------------------------------
     Clones spans until the reel covers 2.5× viewport width,
     then sets --reel-shift so the animation advances by
     exactly one span and loops seamlessly. */
  function initRibbon() {
    var reel = document.querySelector('.ribbon-band .reel');
    if (!reel) return;
    var first = reel.querySelector('span');
    if (!first) return;
    var sw = first.offsetWidth;
    if (!sw) return;
    while (reel.scrollWidth < window.innerWidth * 2.5) {
      reel.appendChild(first.cloneNode(true));
    }
    var shift = -(sw / reel.scrollWidth * 100).toFixed(4);
    reel.style.setProperty('--reel-shift', shift + '%');
  }

  document.addEventListener('DOMContentLoaded', function () {
    wireAddForms();
    wireWish();
    wireVariantPicker();
    wireQtyStepper();
    wireGallery();
    wireCartTriggers();
    wireCartPage();
    wireMobileNav();
    wireHeaderOffset();
    wirePromoCollapse();
    loadRecommendations();
    getCart().then(function (cart) { renderCart(cart, false); });
    initRibbon();
  });
})();
